import { useEffect, useMemo, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, Cell,
} from "recharts";
import {
  Euro, TrendingUp, TrendingDown, Users, ShoppingCart, Target,
  Zap, AlertTriangle, Activity, Info, Calendar, RefreshCcw,
} from "lucide-react";

const PERIODS = [
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
  { key: "90d", label: "90 jours" },
  { key: "12m", label: "12 mois" },
  { key: "ytd", label: "Depuis 1er janvier" },
];

const fmtEur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const fmtEurC = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
const fmtInt = (v) => new Intl.NumberFormat("fr-FR").format(v || 0);
const fmtPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1)}%`);

function Tip({ children }) {
  return (
    <span className="group relative inline-block ml-1 align-middle">
      <Info className="w-3.5 h-3.5 text-slate-500 inline" />
      <span className="pointer-events-none absolute left-1/2 top-full -translate-x-1/2 mt-1 hidden group-hover:block w-64 z-10 bg-slate-900 border border-slate-700 rounded-md p-3 text-[11px] text-slate-300 leading-snug shadow-xl">
        {children}
      </span>
    </span>
  );
}

function Kpi({ label, value, sub, delta, trendUp, icon: Icon, tip, accent = "emerald", testid }) {
  const accents = {
    emerald: { ring: "ring-emerald-500/20", text: "text-emerald-400", bg: "bg-emerald-500/10" },
    blue: { ring: "ring-blue-500/20", text: "text-blue-400", bg: "bg-blue-500/10" },
    amber: { ring: "ring-amber-500/20", text: "text-amber-400", bg: "bg-amber-500/10" },
    rose: { ring: "ring-rose-500/20", text: "text-rose-400", bg: "bg-rose-500/10" },
  };
  const a = accents[accent] || accents.emerald;
  return (
    <div className={`bg-[#0f172a] border border-slate-800 rounded-xl p-5 ring-1 ${a.ring}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center">
            {label}
            {tip && <Tip>{tip}</Tip>}
          </p>
        </div>
        <div className={`w-8 h-8 rounded-md ${a.bg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${a.text}`} />
        </div>
      </div>
      <p className="text-3xl font-semibold text-white tracking-tight">{value}</p>
      <div className="mt-2 flex items-center justify-between text-xs">
        {sub ? <span className="text-slate-500">{sub}</span> : <span />}
        {delta != null && (
          <span className={`inline-flex items-center gap-1 font-mono ${trendUp ? "text-emerald-400" : "text-rose-400"}`}>
            {trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {fmtPct(delta)}
          </span>
        )}
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, right, children, testid, tip }) {
  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-slate-400" />}
          <h3 className="text-sm font-semibold text-slate-200">
            {title}
            {tip && <Tip>{tip}</Tip>}
          </h3>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function EmptyState() {
  return <div className="h-full flex items-center justify-center text-slate-600 text-xs">Aucune donnée sur la période</div>;
}

export default function AdminDashboard() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/dashboard?period=${period}`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const revChart = useMemo(() => (data?.series?.revenue_daily || []).map((r) => ({ date: r.date.slice(5), Revenus: r.value })), [data]);
  const mrrChart = useMemo(() => (data?.series?.mrr_daily || []).map((r) => ({ date: r.date.slice(5), MRR: r.value })), [data]);
  const signupChart = useMemo(() => (data?.series?.signups_daily || []).map((r) => ({ date: r.date.slice(5), Inscriptions: r.value })), [data]);
  const scanChart = useMemo(() => (data?.series?.scans_daily || []).map((r) => ({ date: r.date.slice(5), Scans: r.value })), [data]);

  const byProduct = useMemo(() => {
    const bp = data?.revenue?.by_product || {};
    return Object.entries(bp).map(([k, v]) => ({ name: k === "scan" ? "Scans site internet 20 €" : k === "subscription" ? "Surveillance 200 €" : k, value: v.total_eur, count: v.count }));
  }, [data]);

  const bySource = useMemo(() => {
    const bs = data?.usage?.by_source || {};
    return Object.entries(bs).map(([k, v]) => ({ name: k === "web" ? "URL" : k === "image" ? "Image" : k === "document" ? "Document" : k, Scans: v.count, Pages: v.pages }));
  }, [data]);

  const funnel = data?.funnel?.current;
  const funnelPrev = data?.funnel?.previous;

  const goalArr = data?.goals?.progress_arr_pct || 0;
  const goalClients = data?.goals?.progress_clients_pct || 0;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Console admin · Vue d'ensemble</p>
          <h1 className="text-3xl font-semibold mt-1 text-white">Dashboard</h1>
          <p className="text-sm text-slate-400 mt-1">
            Données Stripe live · Comparaison N-1 · Objectif <span className="text-emerald-400">270 clients / 120 k€ ARR à 24 mois</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-800 bg-[#0f172a] p-1" data-testid="admin-period-selector">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`admin-period-${p.key}`}
                className={`px-3 py-1.5 text-xs rounded transition ${period === p.key ? "bg-[#1F5A99] text-white" : "text-slate-400 hover:text-slate-200"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-refresh-btn"
            className="p-2 rounded-md border border-slate-800 bg-[#0f172a] text-slate-400 hover:text-white"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {data?.revenue?.stripe_unavailable && (
        <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Données Stripe indisponibles — vérifiez la clé Stripe.
        </div>
      )}

      {/* KPI top row : Revenus */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="MRR"
          value={fmtEur(data?.revenue?.mrr_eur)}
          sub={`ARR ${fmtEur(data?.revenue?.arr_eur)}`}
          icon={Euro} accent="emerald" testid="kpi-mrr"
          tip={data?.kpi_formulas?.mrr_eur}
        />
        <Kpi
          label="CA période"
          value={fmtEur(data?.revenue?.period_revenue_eur)}
          sub={`N-1 : ${fmtEur(data?.revenue?.prev_revenue_eur)}`}
          delta={data?.revenue?.revenue_change_pct}
          trendUp={(data?.revenue?.revenue_change_pct ?? 0) >= 0}
          icon={ShoppingCart} accent="blue" testid="kpi-revenue"
          tip={data?.kpi_formulas?.period_revenue_eur}
        />
        <Kpi
          label="Abonnements actifs"
          value={fmtInt(data?.revenue?.active_subs)}
          sub={`${data?.revenue?.past_due || 0} en impayé · ${data?.revenue?.canceled_30d || 0} résil. 30j`}
          icon={Users} accent="amber" testid="kpi-subs"
        />
        <Kpi
          label="Objectif ARR"
          value={`${goalArr.toFixed(1)}%`}
          sub={`Reste ${fmtEur(data?.goals?.gap_arr_eur)} à combler`}
          icon={Target} accent="emerald" testid="kpi-goal-arr"
          tip={data?.kpi_formulas?.gap_arr_eur}
        />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Revenus quotidiens" icon={Euro} testid="panel-revenue-chart" tip="Somme orders.total_eur (paid) par jour. Alimenté par le snapshot nocturne.">
          <div className="h-64" style={{ minWidth: 0 }}>
            {revChart.length === 0 ? <EmptyState /> : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revChart}>
                <defs>
                  <linearGradient id="revG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4CAF6A" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#4CAF6A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: "#0b1220", border: "1px solid #1e293b", borderRadius: 6, fontSize: 12 }} labelStyle={{ color: "#94a3b8" }} formatter={(v) => fmtEurC(v)} />
                <Area type="monotone" dataKey="Revenus" stroke="#4CAF6A" fill="url(#revG)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </Panel>

        <Panel title="MRR quotidien" icon={TrendingUp} testid="panel-mrr-chart" tip="Snapshot Stripe : MRR calculé à partir des abonnements actifs.">
          <div className="h-64" style={{ minWidth: 0 }}>
            {mrrChart.length === 0 ? <EmptyState /> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mrrChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: "#0b1220", border: "1px solid #1e293b", borderRadius: 6, fontSize: 12 }} labelStyle={{ color: "#94a3b8" }} formatter={(v) => fmtEurC(v)} />
                <Line type="monotone" dataKey="MRR" stroke="#1F5A99" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            )}
          </div>
        </Panel>

        <Panel title="Progression objectifs" icon={Target} testid="panel-goals" tip="Objectif : 270 clients Surveillance et 120 k€ ARR à 24 mois.">
          <div className="h-64 flex items-center justify-center" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="30%" outerRadius="90%" data={[
                { name: "Clients", value: Math.min(100, goalClients), fill: "#4CAF6A" },
                { name: "ARR", value: Math.min(100, goalArr), fill: "#1F5A99" },
              ]} startAngle={90} endAngle={-270}>
                <RadialBar background dataKey="value" cornerRadius={8} />
                <Tooltip contentStyle={{ backgroundColor: "#0b1220", border: "1px solid #1e293b", borderRadius: 6, fontSize: 12 }} formatter={(v) => `${v.toFixed(1)}%`} />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-2 text-center text-xs">
            <div><p className="text-emerald-400 font-semibold">{data?.goals?.current_clients || 0}/{data?.goals?.target_clients || 270}</p><p className="text-slate-500">Clients</p></div>
            <div><p className="text-blue-400 font-semibold">{fmtEur(data?.goals?.current_arr_eur)}/{fmtEur(data?.goals?.target_arr_eur)}</p><p className="text-slate-500">ARR</p></div>
          </div>
        </Panel>
      </div>

      {/* Funnel */}
      <Panel title="Funnel gratuit → payant" icon={Zap} testid="panel-funnel" tip="Cascade des étapes clés sur la période courante vs période précédente (N-1).">
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { label: "Scans gratuits", cur: funnel?.free_scans, prev: funnelPrev?.free_scans, delta: data?.funnel?.free_scans_change_pct, color: "text-slate-200" },
            { label: "Inscriptions", cur: funnel?.signups, prev: funnelPrev?.signups, delta: data?.funnel?.signups_change_pct, color: "text-blue-400" },
            { label: "Scans site internet payés (20 €)", cur: funnel?.paid_scans, prev: funnelPrev?.paid_scans, color: "text-emerald-400" },
            { label: "Abonnements souscrits", cur: funnel?.subscriptions, prev: funnelPrev?.subscriptions, color: "text-emerald-400" },
          ].map((s) => (
            <div key={s.label} className="p-4 border border-slate-800 rounded-lg" data-testid={`funnel-step-${s.label.slice(0,6)}`}>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">{s.label}</p>
              <p className={`text-3xl font-semibold ${s.color}`}>{fmtInt(s.cur)}</p>
              <p className="text-xs text-slate-500 mt-1">N-1 : {fmtInt(s.prev)}</p>
              {s.delta != null && (
                <p className={`text-xs font-mono mt-1 ${s.delta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtPct(s.delta)}</p>
              )}
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div className="p-3 bg-[#0b1220] rounded-md text-xs text-slate-400 flex items-center justify-between">
            <span>Conversion gratuit → payé</span>
            <span className="font-mono text-emerald-400 text-base">{funnel?.conv_free_to_paid_pct ?? 0}%</span>
          </div>
          <div className="p-3 bg-[#0b1220] rounded-md text-xs text-slate-400 flex items-center justify-between">
            <span>Conversion inscription → payé</span>
            <span className="font-mono text-blue-400 text-base">{funnel?.conv_signup_to_paid_pct ?? 0}%</span>
          </div>
        </div>
      </Panel>

      {/* Usage & CA par produit */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Scans par source" icon={Activity} testid="panel-usage-source">
          <div className="h-64" style={{ minWidth: 0 }}>
            {bySource.length === 0 ? <EmptyState /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bySource}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: "#0b1220", border: "1px solid #1e293b", borderRadius: 6, fontSize: 12 }} labelStyle={{ color: "#94a3b8" }} />
                <Bar dataKey="Scans" radius={[6, 6, 0, 0]}>
                  {bySource.map((s, i) => <Cell key={s.name || `bs-${i}`} fill={["#4CAF6A", "#1F5A99", "#f59e0b"][i % 3]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
            <div className="text-center"><p className="text-slate-500">Total scans</p><p className="font-semibold">{fmtInt(data?.usage?.total_scans)}</p></div>
            <div className="text-center"><p className="text-slate-500">Pages crawlées</p><p className="font-semibold">{fmtInt(data?.usage?.pages_crawled)}</p></div>
            <div className="text-center"><p className="text-slate-500">Coût LLM estimé</p><p className="font-semibold text-amber-400">{fmtEurC(data?.usage?.est_llm_cost_eur)}</p></div>
          </div>
        </Panel>

        <Panel title="Marge & CA par produit" icon={Euro} testid="panel-margin" tip="Marge réelle = (CA − coûts_LLM_estimés − coûts_infra_estimés) / CA. Marge théorique cible : 94 %.">
          <div className="h-64" style={{ minWidth: 0 }}>
            {byProduct.length === 0 ? <EmptyState /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byProduct} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#64748b", fontSize: 10 }} width={110} />
                <Tooltip contentStyle={{ backgroundColor: "#0b1220", border: "1px solid #1e293b", borderRadius: 6, fontSize: 12 }} labelStyle={{ color: "#94a3b8" }} formatter={(v) => fmtEurC(v)} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {byProduct.map((p, i) => <Cell key={p.name || `bp-${i}`} fill={["#4CAF6A", "#1F5A99"][i % 2]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
            <div className="p-3 bg-[#0b1220] rounded-md">
              <p className="text-slate-500">Marge réelle estimée</p>
              <p className="text-lg font-semibold text-emerald-400">
                {data?.usage?.real_margin_pct == null ? "—" : `${data.usage.real_margin_pct}%`}
              </p>
            </div>
            <div className="p-3 bg-[#0b1220] rounded-md">
              <p className="text-slate-500">Marge cible</p>
              <p className="text-lg font-semibold text-slate-300">{data?.usage?.theoretical_margin_pct}%</p>
            </div>
          </div>
        </Panel>
      </div>

      {/* Santé + Inscriptions/Scans daily */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Santé opérationnelle" icon={AlertTriangle} testid="panel-health">
          <div className="space-y-3 text-sm">
            {[
              { l: "File d'attente paiements", v: data?.health?.queue_pending, tone: "text-blue-400" },
              { l: "Paiements en échec", v: data?.health?.queue_failed, tone: "text-rose-400" },
              { l: "Erreurs de fulfilment", v: data?.health?.fulfil_errors_total, tone: "text-rose-400" },
              { l: "Preuves expirant < 30 j", v: data?.health?.expiring_proofs_30d, tone: "text-amber-400" },
              { l: "Renouv. Stripe J-30", v: data?.revenue?.renewals_j30, tone: "text-emerald-400" },
              { l: "Renouv. Stripe J-60", v: data?.revenue?.renewals_j60, tone: "text-emerald-400" },
            ].map((r) => (
              <div key={r.l} className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-400">{r.l}</span>
                <span className={`font-mono ${r.tone}`}>{fmtInt(r.v)}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Inscriptions" icon={Users} testid="panel-signups" tip="Nouveaux comptes clients (hors admins) par jour.">
          <div className="h-56" style={{ minWidth: 0 }}>
            {signupChart.length === 0 ? <EmptyState /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={signupChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: "#0b1220", border: "1px solid #1e293b", borderRadius: 6, fontSize: 12 }} labelStyle={{ color: "#94a3b8" }} />
                <Bar dataKey="Inscriptions" fill="#1F5A99" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </Panel>

        <Panel title="Scans totaux" icon={Activity} testid="panel-scans">
          <div className="h-56" style={{ minWidth: 0 }}>
            {scanChart.length === 0 ? <EmptyState /> : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={scanChart}>
                <defs>
                  <linearGradient id="scanG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: "#0b1220", border: "1px solid #1e293b", borderRadius: 6, fontSize: 12 }} labelStyle={{ color: "#94a3b8" }} />
                <Area type="monotone" dataKey="Scans" stroke="#f59e0b" fill="url(#scanG)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </Panel>
      </div>

      <p className="text-[10px] text-slate-600 flex items-center justify-end gap-1">
        <Calendar className="w-3 h-3" />
        Période : {data?.range?.start?.slice(0,10)} → {data?.range?.end?.slice(0,10)} · Comparaison N-1 : {data?.range?.prev_start?.slice(0,10)} → {data?.range?.prev_end?.slice(0,10)}
      </p>
    </div>
  );
}
